/**
 * The Carry machine: one state machine for every way of moving a thing onto
 * another thing (§5.3).
 *
 * A pointer drag, a click that carries and a click that drops, and Space /
 * arrows / Enter on the keyboard are three roads through the same states, and
 * every drop goes through one function, `drop`, which asks the verbs table
 * for the verb and its Args. So the three roads send identical Args and
 * announce identical words; only the way in differs.
 *
 * Pure: inputs in, state and effects out. Effects are what the page does
 * with them (fire, confirm, need, announce, presence, ghost, ring); nothing
 * here touches the DOM, the network or a clock.
 */

import type {
  CarryEffect, CarryFn, CarryInput, CarryState, Held, HeldKind, Modifier, Origin, Persona, PresenceIn, Target, Verb, VerbContext,
} from '../contracts';
import { fits, isHeld, isNeed, nouns, num, pick, ringOf, str, targetLabel, TOOL_KINDS, type HandVerb } from './verbs';
import { objectOf } from '../model/json';

/** Pixels a pointer moves before a press becomes a drag (§3.1). */
export const THRESHOLD = 4;
/** Milliseconds a finger rests before it lifts (§3.1). */
export const LONG_PRESS = 350;

export interface CarryCtx extends VerbContext {
  readonly verbs: readonly Verb[];
  targets(): readonly Target[];
  /** The seq a World is scrubbed back to, or null while it is Live (§5.5 rule 9). */
  scrubbed?(world: string): number | null;
  /** Whether an operation writes; a write on a scrubbed World returns it to Live first. */
  writes?(op: string): boolean;
  /** Targets that stand as one on the keyboard (a field's cells), and which of them leads. */
  groupOf?(t: Target): string | null;
  leadOf?(group: string): string | null;
  /** A screen's own drop where no verb fits (Gestures.local): its words, else null. */
  local?(held: Held, t: Target): string | null;
}

/**
 * Inputs beyond the contract's: a keyboard lift bound to one verb, a ring
 * entry chosen by index, a re-judge (targets came or went, or a peer's
 * commit changed what one will take), and E on the keyboard: ask the door
 * about the target in focus, and write nothing if it has nothing to explain.
 */
export type HandInput =
  | CarryInput
  | { type: 'key-lift'; held: Held; only?: string }
  | { type: 'choose'; index: number }
  | { type: 'refresh' }
  | { type: 'key-explain' };

/** What the machine keeps beside the contract's fields. */
interface Inner extends CarryState {
  readonly press?: { readonly x: number; readonly y: number; readonly pointer: 'mouse' | 'touch' | 'pen' };
  readonly at?: { readonly x: number; readonly y: number };
  readonly only?: string;
  readonly ring?: readonly Verb[];
  readonly subject?: Held | Target;
}

/** A step: the next state, the effects, and a World to return to Live (a write dropped on it while it was scrubbed). */
export interface Step { readonly state: CarryState; readonly effects: CarryEffect[]; readonly live?: string }

export const idle = (mods: ReadonlySet<Modifier> = new Set()): CarryState => ({ phase: 'idle', mods, candidates: [], index: 0 });

/** Extra fields of the inner state, for the page (ring subject, the verb a shortcut lifted for). */
export const inner = (s: CarryState): { ring: readonly Verb[]; subject: Held | Target | null; only: string | null; at: Origin } => {
  const x = s as Inner;
  return { ring: x.ring ?? [], subject: x.subject ?? null, only: x.only ?? null, at: x.at ?? null };
};

// ---------- presence: what peers may see of a carry ----------

const WHAT: Partial<Record<HeldKind, 'occupier' | 'run' | 'softRun' | 'spark' | 'braid' | 'bead' | 'card'>> = {
  occupier: 'occupier', run: 'run', softRun: 'softRun', spark: 'spark', braid: 'braid', bead: 'bead', card: 'card', toolCard: 'card',
};
/** Things whose labels could carry a secret: peers see only what kind of thing it is. */
const SECRET_KINDS: Partial<Record<HeldKind, string>> = { key: 'a key', blankKey: 'a new key', capsule: 'a capsule', link: 'a mailed link' };
const SECRETS = /\b(?:sbk|sbo|sbv|whsec|wt)_[A-Za-z0-9_.-]+|sk-ant-[A-Za-z0-9_-]+/g;

export const safeLabel = (h: Held): string =>
  (SECRET_KINDS[h.kind] ?? h.label.replace(SECRETS, '••••')).slice(0, 64);

const place = (o: ReturnType<typeof objectOf>): { date?: string; hour?: number } => {
  const out: { date?: string; hour?: number } = {};
  const date = str(o, 'date');
  const hour = num(o, 'hour');
  if (date !== undefined && /^\d{4}-\d{2}-\d{2}$/.test(date)) out.date = date;
  if (hour !== undefined && hour >= 0 && hour <= 23) out.hour = hour;
  return out;
};

const liftFrame = (h: Held, ops: readonly string[]): PresenceIn => {
  const o = objectOf(h.data);
  const slots = str(o, 'slots');
  return {
    t: 'lift', what: WHAT[h.kind] ?? 'other', label: safeLabel(h),
    ...(ops.length === 1 ? { op: ops[0]!.slice(0, 64) } : {}),
    ...place(o),
    ...(slots && /^0x[0-9a-f]+$/.test(slots) ? { slots } : {}),
  };
};

const overFrame = (t: Target): PresenceIn => {
  const o = objectOf(t.data);
  const slots = str(o, 'slots');
  const slot = num(o, 'slot');
  const mask = slots && /^0x[0-9a-f]+$/.test(slots) ? slots : slot !== undefined && slot >= 0 ? `0x${(1n << BigInt(slot)).toString(16)}` : undefined;
  const at = place(o);
  return {
    t: 'over', ...at,
    ...(mask ? { slots: mask } : {}),
    ...(at.date === undefined ? { lane: `${t.kind}${t.world ? `:${t.world}` : ''}`.slice(0, 64) } : {}),
  };
};

// ---------- judging a target ----------

const verbsFor = (s: Inner, ctx: CarryCtx): readonly Verb[] => (s.only ? ctx.verbs.filter((v) => v.id === s.only) : ctx.verbs);

/** The seq a World is scrubbed back to, when a write of `op` would land on it; null when it is Live or `op` reads. */
const scrubbedFor = (world: string | undefined, op: string, ctx: CarryCtx): number | null =>
  world && (ctx.writes?.(op) ?? true) ? ctx.scrubbed?.(world) ?? null : null;

const verdictOf = (s: Inner, t: Target, ctx: CarryCtx): { verdict: 'ok' | 'maybe' | 'no'; words: string } => {
  const held = s.held!;
  const choice = pick(verbsFor(s, ctx), held, t, s.mods, ctx);
  if (!choice.verb) {
    const own = s.only ? null : ctx.local?.(held, t) ?? null;
    return own ? { verdict: 'ok', words: own } : { verdict: 'no', words: `${targetLabel(t)}: ${choice.why}` };
  }
  const seq = scrubbedFor(t.world ?? held.world, choice.verb.op, ctx);
  if (seq !== null) return { verdict: 'no', words: `${choice.verb.label(held, t)} (scrubbed back to #${seq}: a drop here returns to Live and sends nothing)` };
  const preview = choice.verb.preview?.(held, t, ctx) ?? 'ok';
  const hint = preview === 'maybe' ? ' (the door decides)' : preview === 'no' ? ' (the door will likely refuse)' : '';
  return { verdict: preview, words: `${choice.verb.label(held, t)}${hint}` };
};

const accepting = (held: Held, mods: ReadonlySet<Modifier>, only: string | undefined, ctx: CarryCtx): Target[] => {
  const verbs = only ? ctx.verbs.filter((v) => v.id === only) : ctx.verbs;
  const any = verbs.filter((v) => v.held === held.kind);
  const local = only ? undefined : ctx.local;
  if (any.length === 0 && !local) return [];
  const all = ctx.targets().filter((t) => (any.some((v) => fits(v, held, t)) && pick(verbs, held, t, mods, ctx).verb !== null)
    || (!!local && local(held, t) !== null && pick(verbs, held, t, mods, ctx).verb === null));
  if (!ctx.groupOf) return all;
  // A group (a field's cells) is one stop on the keyboard: its lead cell if that accepts, else its first that does.
  const out: Target[] = [];
  const at = new Map<string, number>();
  for (const t of all) {
    const group = ctx.groupOf(t);
    if (!group) {
      out.push(t);
      continue;
    }
    const i = at.get(group);
    if (i === undefined) {
      at.set(group, out.length);
      out.push(t);
    } else if (ctx.leadOf?.(group) === t.id) {
      out[i] = t;
    }
  }
  return out;
};

/** A presence frame, on the socket of the World it is about (a target's World, else the held thing's). */
const said = (frame: PresenceIn, ...about: (Held | Target | null | undefined)[]): CarryEffect => {
  const world = about.find((x) => x?.world)?.world;
  return { type: 'presence', frame, ...(world ? { world } : {}) };
};

/** Whose key sends a drop: the target's persona, else the held thing's (none: the World in its Args decides). */
const personaOf = (held: Held, t: Target): { persona?: Persona } => {
  const persona = t.persona ?? held.persona;
  return persona ? { persona } : {};
};

const opsOf = (held: Held, ctx: CarryCtx): string[] =>
  [...new Set(ctx.verbs.filter((v) => v.held === held.kind).map((v) => v.op))];

// ---------- transitions ----------

const lift = (
  s: Inner, held: Held, phase: 'dragging' | 'carrying' | 'keyboard', ctx: CarryCtx,
  opts: { at?: { x: number; y: number }; over?: Target | null; only?: string } = {},
): Step => {
  // A lift for one verb (a shortcut, a ring entry) holds that verb's modifiers: Q's queue, E's explain.
  const wants = opts.only ? ctx.verbs.find((v) => v.id === opts.only)?.mods ?? [] : [];
  const mods: ReadonlySet<Modifier> = wants.some((m) => !s.mods.has(m)) ? new Set([...s.mods, ...wants]) : s.mods;
  const base: Inner = { phase, held, mods, candidates: [], index: 0, ...(opts.only ? { only: opts.only } : {}) };
  const candidates = accepting(held, mods, opts.only, ctx);
  const n = candidates.length;
  const effects: CarryEffect[] = [
    said(liftFrame(held, opsOf(held, ctx)), held),
    { type: 'ghost', held, ...(opts.at ? { at: opts.at } : {}) },
  ];
  const told = `Lifted ${held.label}. ${n === 0 ? 'Nothing here takes it' : `${n} ${n === 1 ? 'place takes' : 'places take'} it`}.`;
  let state: Inner = { ...base, candidates, ...(opts.at ? { at: opts.at } : {}) };
  if (phase === 'keyboard' && n > 0) {
    const over = candidates[0]!;
    const v = verdictOf(state, over, ctx);
    state = { ...state, over, verdict: v.verdict, index: 0 };
    effects.push({ type: 'announce', text: `${told} ${v.words}`, assertive: false }, said(overFrame(over), over, held));
    return { state, effects };
  }
  effects.push({ type: 'announce', text: told, assertive: false });
  if (opts.over) {
    const moved = aim(state, opts.over, opts.at, ctx);
    return { state: moved.state, effects: [...effects, ...moved.effects] };
  }
  return { state, effects };
};

/** The pointer (or the keyboard) is now over `t`. */
const aim = (s: Inner, t: Target | null, at: { x: number; y: number } | undefined, ctx: CarryCtx): Step => {
  const effects: CarryEffect[] = [];
  const same = (t?.id ?? null) === (s.over?.id ?? null);
  let state: Inner = { ...s, ...(at ? { at } : {}) };
  if (!same) {
    if (t) {
      const v = verdictOf(s, t, ctx);
      const index = s.candidates.findIndex((c) => c.id === t.id);
      state = { ...state, over: t, verdict: v.verdict, index: index < 0 ? s.index : index };
      effects.push(said(overFrame(t), t, s.held));
      // A pointer sweeping across a field passes dozens of places that do not take the thing: only those that do
      // are said. The keyboard, which stops on one place at a time, hears every one, refusals included.
      if (s.phase !== 'dragging' || v.verdict !== 'no') effects.push({ type: 'announce', text: v.words, assertive: false });
    } else {
      const { over: _o, verdict: _v, ...rest } = state;
      state = rest;
    }
  } else if (t && s.over && t !== s.over) {
    // The same target, read again (a brush anchored under the pointer): keep what it says now, quietly.
    state = { ...state, over: t, verdict: verdictOf(s, t, ctx).verdict };
  }
  if (at && s.held) effects.push({ type: 'ghost', held: s.held, at });
  return { state, effects };
};

const putBack = (s: Inner, words?: string, assertive = false): Step => {
  const effects: CarryEffect[] = [];
  if (s.held) {
    effects.push(
      said({ t: 'end', how: 'cancel' }, s.over, s.held),
      { type: 'ghost', held: null, home: true },
      { type: 'announce', text: words ?? `Put back ${s.held.label}.`, assertive },
    );
  } else if (s.phase === 'ring') {
    effects.push({ type: 'announce', text: 'Closed the verbs.', assertive: false });
  }
  return { state: idle(), effects };
};

/** The one place a drop is decided: every road ends here. */
const drop = (s: Inner, t: Target | null, ctx: CarryCtx): Step => {
  const held = s.held;
  if (!held) return { state: idle(), effects: [] };
  if (!t) return putBack(s);
  const choice = pick(verbsFor(s, ctx), held, t, s.mods, ctx);
  if (!choice.verb) {
    // A screen's own drop: the hand lets go here and the screen acts (Gestures.local, watchDrops).
    const own = s.only ? null : ctx.local?.(held, t) ?? null;
    if (own) {
      return {
        state: idle(),
        effects: [
          { type: 'announce', text: own, assertive: false },
          said({ t: 'end', how: 'drop' }, t, held),
          { type: 'ghost', held: null },
        ],
      };
    }
    return putBack(s, `${held.label} was not dropped: ${choice.why}.`, true);
  }
  const verb = choice.verb;
  const world = t.world ?? held.world;
  // §5.5 rule 9: while scrubbed, writes are off. What the hand lifted was read off the past, so nothing is
  // sent: the World returns to Live, the thing goes home, and the hand drops again on what stands now.
  const seq = scrubbedFor(world, verb.op, ctx);
  if (world && seq !== null) {
    const back = putBack(s, `${verb.label(held, t)}: not sent. ${world} was scrubbed back to #${seq}; it is Live again, so drop again on what stands now.`, true);
    return { ...back, live: world };
  }
  const effects: CarryEffect[] = [];
  if ((s.over?.id ?? null) !== t.id) effects.push(said(overFrame(t), t, held));
  const built = verb.args(held, t, ctx);
  const origin: Origin = s.at ?? null;
  const who = personaOf(held, t);
  if (isNeed(built)) {
    effects.push({ type: 'need', verb, holes: built.need, partial: built.partial, ...who });
  } else if (verb.confirm) {
    effects.push({ type: 'confirm', verb, args: built, origin, ...who });
  } else {
    effects.push({ type: 'fire', verb, args: built, origin, ...who });
    for (const more of (verb as Partial<HandVerb>).also?.(held, t, ctx) ?? []) effects.push({ type: 'fire', verb, args: more, origin, ...who });
  }
  effects.push(
    { type: 'announce', text: `${verb.label(held, t)}${isNeed(built) ? ': fill in the rest on its card' : ''}`, assertive: false },
    said({ t: 'end', how: 'drop' }, t, held),
    { type: 'ghost', held: null },
  );
  return { state: idle(), effects };
};

const cycle = (s: Inner, dir: 1 | -1, ctx: CarryCtx): Step => {
  const n = s.candidates.length;
  if (n === 0) return { state: s, effects: [{ type: 'announce', text: 'Nothing here takes it.', assertive: false }] };
  // From where the aim is now (the candidates may have changed since the lift), else from the last stop.
  const here = s.over ? s.candidates.findIndex((c) => c.id === s.over!.id) : -1;
  const index = (((s.over ? (here >= 0 ? here : s.index) + dir : dir > 0 ? 0 : n - 1) % n) + n) % n;
  const t = s.candidates[index]!;
  const moved = aim({ ...s, index }, t, undefined, ctx);
  return { state: { ...moved.state, index }, effects: moved.effects };
};

const openRing = (subject: Held | Target, ctx: CarryCtx): Step => {
  // A held thing's ring lists only what something on screen can take: a verb whose places are all elsewhere is not offered here.
  const ring = isHeld(subject)
    ? ringOf(ctx.verbs, subject).filter((v) => ctx.targets().some((t) => fits(v, subject, t)))
    : ringOf(ctx.verbs, subject);
  const name = isHeld(subject) ? subject.label : targetLabel(subject);
  const state: Inner = { phase: 'ring', mods: new Set(), candidates: [], index: 0, ring, subject };
  const first = ring[0];
  return {
    state,
    effects: [
      { type: 'ring', verbs: ring, subject },
      {
        type: 'announce', assertive: false,
        text: ring.length === 0 ? `Nothing to do with ${name}.` : `${ring.length} ${ring.length === 1 ? 'verb' : 'verbs'} for ${name}. ${ringWords(first!)}`,
      },
    ],
  };
};

const ringWords = (v: Verb): string => `${v.title ?? v.id} · ${v.op}`;

/** A held of the verb's kind with nothing in it, for a verb chosen from a target's ring. */
const emptyHeld = (kind: HeldKind): Held => ({ kind, id: `empty:${kind}`, label: `a ${kind}`, data: {} });

const chooseRing = (s: Inner, index: number, ctx: CarryCtx): Step => {
  const verb = s.ring?.[index];
  const subject = s.subject;
  if (!verb || !subject) return putBack(s);
  if (isHeld(subject)) return lift({ ...idle(s.mods) } as Inner, subject, 'keyboard', ctx, { only: verb.id });
  if (TOOL_KINDS.has(verb.held)) {
    const tool = nouns.tool(verb.held as 'lens' | 'needle' | 'tap');
    return drop({ phase: 'keyboard', held: tool, mods: s.mods, candidates: [], index: 0, only: verb.id, over: subject }, subject, ctx);
  }
  const built = verb.args(emptyHeld(verb.held), subject, ctx);
  const who = subject.persona ? { persona: subject.persona } : {};
  const effects: CarryEffect[] = isNeed(built)
    ? [{ type: 'need', verb, holes: built.need, partial: built.partial, ...who }]
    : [{ type: 'need', verb, holes: [], partial: built, ...who }];
  effects.push({ type: 'announce', text: `${ringWords(verb)}: fill in the rest on its card`, assertive: false });
  return { state: idle(), effects };
};

/** One step of the machine. */
export function step(s0: CarryState, i: HandInput, ctx: CarryCtx): Step {
  const s = s0 as Inner;
  switch (i.type) {
    case 'mod': {
      const mods = new Set(s.mods);
      if (i.on) mods.add(i.mod);
      else mods.delete(i.mod);
      if (mods.size === s.mods.size && [...mods].every((m) => s.mods.has(m))) return { state: s, effects: [] };
      if (!s.held) return { state: { ...s, mods }, effects: [] };
      const next: Inner = { ...s, mods, candidates: accepting(s.held, mods, s.only, ctx) };
      const effects: CarryEffect[] = [{ type: 'announce', text: `${i.mod} ${i.on ? 'on' : 'off'}.`, assertive: false }];
      if (next.over) {
        const v = verdictOf(next, next.over, ctx);
        effects.push({ type: 'announce', text: v.words, assertive: false });
        return { state: { ...next, verdict: v.verdict }, effects };
      }
      return { state: next, effects };
    }
    case 'refresh': {
      if (!s.held || s.phase === 'pressed' || s.phase === 'ring') return { state: s, effects: [] };
      const candidates = accepting(s.held, s.mods, s.only, ctx);
      const here = s.over ? candidates.findIndex((c) => c.id === s.over!.id) : -1;
      const next: Inner = { ...s, candidates, index: here >= 0 ? here : Math.min(s.index, Math.max(0, candidates.length - 1)) };
      if (!next.over) {
        // A keyboard lift that found nothing aims at the first place that takes it once one appears (ticks after a read).
        if (s.phase !== 'keyboard' || candidates.length === 0) return { state: next, effects: [] };
        const moved = aim({ ...next, index: 0 }, candidates[0]!, undefined, ctx);
        return { state: { ...moved.state, index: 0 }, effects: moved.effects };
      }
      const v = verdictOf(next, next.over, ctx);
      const effects: CarryEffect[] = v.verdict !== s.verdict ? [{ type: 'announce', text: v.words, assertive: v.verdict === 'no' }] : [];
      return { state: { ...next, verdict: v.verdict }, effects };
    }
    case 'key-explain': {
      if ((s.phase !== 'keyboard' && s.phase !== 'carrying') || !s.held) return { state: s, effects: [] };
      if (!s.over) return { state: s, effects: [{ type: 'announce', text: 'Aim at something first: E asks the door about it.', assertive: false }] };
      const mods = new Set<Modifier>([...s.mods, 'explain']);
      const trial: Inner = { ...s, mods };
      const choice = pick(verbsFor(trial, ctx), s.held, s.over, mods, ctx);
      // Nothing to explain there: say so and keep carrying. E never writes.
      if (!choice.verb) return { state: s, effects: [{ type: 'announce', text: `${targetLabel(s.over)}: ${choice.why}.`, assertive: true }] };
      return drop(trial, s.over, ctx);
    }
    case 'cancel':
      return s.phase === 'idle' ? { state: s, effects: [] } : putBack(s);
    case 'press':
      if (s.phase !== 'idle') return { state: s, effects: [] };
      return { state: { phase: 'pressed', held: i.held, mods: s.mods, candidates: [], index: 0, press: { ...i.at, pointer: i.pointer }, at: i.at } as Inner, effects: [] };
    case 'move': {
      if (s.phase === 'pressed' && s.press && s.held) {
        const far = Math.hypot(i.at.x - s.press.x, i.at.y - s.press.y) >= THRESHOLD;
        if (!far) return { state: { ...s, at: i.at } as Inner, effects: [] };
        if (s.press.pointer === 'touch') return { state: idle(s.mods), effects: [] }; // the finger scrolled
        return lift(s, s.held, 'dragging', ctx, { at: i.at, over: i.over });
      }
      if (s.phase === 'dragging' || s.phase === 'carrying' || s.phase === 'keyboard') return aim(s, i.over, i.at, ctx);
      return { state: s, effects: [] };
    }
    case 'timer':
      if (s.phase === 'pressed' && s.held && s.press?.pointer === 'touch' && i.elapsed >= LONG_PRESS) {
        return lift(s, s.held, 'dragging', ctx, { at: { x: s.press.x, y: s.press.y } });
      }
      return { state: s, effects: [] };
    case 'release':
      if (s.phase === 'pressed' && s.held) return lift(s, s.held, 'carrying', ctx, s.at ? { at: s.at } : {});
      if (s.phase === 'dragging') return drop(s, s.over ?? null, ctx);
      return { state: s, effects: [] };
    case 'click': {
      if ('held' in i.thing) {
        const held = i.thing.held;
        if ((s.phase === 'carrying' || s.phase === 'keyboard') && s.held?.id === held.id) return putBack(s);
        return lift({ ...s, over: undefined } as Inner, held, 'carrying', ctx, s.at ? { at: s.at } : {});
      }
      if (s.phase === 'carrying' || s.phase === 'keyboard' || s.phase === 'dragging') return drop(s, i.thing.target, ctx);
      return { state: s, effects: [] };
    }
    case 'key-lift': {
      const only = 'only' in i ? i.only : undefined;
      return lift(s.phase === 'idle' || s.phase === 'ring' ? (idle(s.mods) as Inner) : s, i.held, 'keyboard', ctx, only ? { only } : {});
    }
    case 'key-cycle':
      if (s.phase === 'ring') {
        const n = s.ring?.length ?? 0;
        if (n === 0) return { state: s, effects: [] };
        const index = (((s.index + i.dir) % n) + n) % n;
        return { state: { ...s, index }, effects: [{ type: 'announce', text: ringWords(s.ring![index]!), assertive: false }] };
      }
      if (s.phase === 'keyboard' || s.phase === 'carrying' || s.phase === 'dragging') return cycle(s, i.dir, ctx);
      return { state: s, effects: [] };
    case 'key-drop':
      if (s.phase === 'ring') return chooseRing(s, s.index, ctx);
      if (s.phase === 'keyboard' || s.phase === 'carrying') return drop(s, s.over ?? null, ctx);
      return { state: s, effects: [] };
    case 'key-ring':
      if (s.phase !== 'idle' && s.phase !== 'ring') return { state: s, effects: [] };
      return openRing(i.subject, ctx);
    case 'choose':
      if (s.phase !== 'ring') return { state: s, effects: [] };
      return chooseRing(s, i.index, ctx);
    default:
      return { state: s, effects: [] };
  }
}

/** The contract's name for it. */
export const carry: CarryFn = (s, i, ctx) => step(s, i, ctx as CarryCtx);
